import { useState, type FormEvent, type ReactNode } from 'react';
import { settingsGroupApi, type SettingsGroupView } from '../../api/client';
import { Button, Card, Chip, Field, LoadError, Loading, Notice } from '../../components/ui';
import { shortWhen } from '../../lib/format';
import { useData } from '../../lib/useData';

/**
 * Stitch Admin → System: a group of settings kept in Stitch instead of EasyPanel's variables (1.13.0,
 * Teampace 2845). Each says where it comes from; secrets are sealed when saved and never shown again
 * (leave the box empty to keep one). A change takes effect within seconds, with no redeploy.
 */
export interface SettingsFieldLabel {
  key: string;
  label: string;
  placeholder?: string;
  hint?: ReactNode;
}

export function SettingsGroupCard({
  title,
  path,
  intro,
  labels,
  onLabel,
  offLabel,
  restartWhat
}: {
  title: string;
  path: string;
  intro: ReactNode;
  labels: SettingsFieldLabel[];
  /** The chip when it's in use, and when it isn't (e.g. "Patches on"). */
  onLabel: string;
  offLabel: string;
  /** What starts after a restart, when it's saved while off (e.g. "Patches"). */
  restartWhat: string;
}) {
  const api = settingsGroupApi(path);
  const data = useData(() => api.get(), [path]);
  const [view, setView] = useState<SettingsGroupView | null>(null);
  const v = view ?? data.data;
  const [typed, setTyped] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);

  if (data.error) {
    return (
      <Card title={title}>
        <LoadError error={data.error} retry={data.reload} />
      </Card>
    );
  }
  if (!v) {
    return (
      <Card title={title}>
        <Loading />
      </Card>
    );
  }

  async function run(key: string, fn: () => Promise<void>) {
    setBusy(key);
    setProblem(null);
    setNote(null);
    try {
      await fn();
    } catch (err) {
      setProblem(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy('');
    }
  }

  const fieldOf = (key: string) => v.fields.find((f) => f.key === key)!;
  const shown = (key: string) => {
    const f = fieldOf(key);
    return typed[key] ?? (f.secret ? '' : (f.value ?? ''));
  };
  const change = () => {
    const out: Record<string, string> = {};
    for (const l of labels) {
      const f = fieldOf(l.key);
      const t = typed[l.key];
      if (t === undefined) continue;
      if (f.secret ? t.trim() : t.trim() !== (f.value ?? '')) out[l.key] = t.trim();
    }
    return out;
  };
  const changed = Object.keys(change()).length > 0;
  const stillInEasyPanel = v.easypanel.filter((x) => !x.inStitch);
  const canDelete = v.easypanel.filter((x) => x.inStitch);
  const saved = v.fields.filter((f) => f.source === 'stitch');

  async function save(e: FormEvent) {
    e.preventDefault();
    await run('save', async () => {
      setView(await api.save(change()));
      setTyped({});
      setNote({ ok: true, text: 'Saved. Stitch uses it within a few seconds.' });
    });
  }

  return (
    <Card title={title}>
      <form className="card-body" onSubmit={(e) => void save(e)}>
        <p className="muted" style={{ marginTop: 0 }}>
          {intro}
        </p>
        <div style={{ marginBottom: 12 }}>{v.active ? <Chip tone="ok">{onLabel}</Chip> : <Chip>{offLabel}</Chip>}</div>
        {v.restartNeeded && (
          <Notice tone="warn" title={`Restart Stitch once to start ${restartWhat}`}>
            {restartWhat} were off when Stitch started. Restart the Stitch app in EasyPanel once, and it uses these from then on. Later changes need no restart.
          </Notice>
        )}
        {!v.canSeal && (
          <Notice tone="warn" title="Secrets can’t be saved here yet">
            Set DATA_ENCRYPTION_KEY in EasyPanel and redeploy once. It locks secrets before they’re saved.
          </Notice>
        )}
        {v.fields.some((f) => f.unreadable) && <Notice tone="crit">A saved secret can’t be opened with this DATA_ENCRYPTION_KEY. Save it again.</Notice>}
        {stillInEasyPanel.length > 0 && (
          <Notice tone="neutral" title="Still set in EasyPanel">
            <p style={{ margin: '0 0 8px' }}>{stillInEasyPanel.map((x) => x.name).join(', ')}. Move them here once, then delete them from EasyPanel.</p>
            <Button
              icon="download"
              disabled={busy !== ''}
              onClick={() =>
                void run('move', async () => {
                  const r = await api.move();
                  setView(r.settings);
                  setNote({
                    ok: true,
                    text: [r.moved.length ? `Moved into Stitch: ${r.moved.join(', ')}.` : 'Nothing to move.', r.kept.length ? `${r.kept.join(' and ')} stay in EasyPanel until DATA_ENCRYPTION_KEY is set.` : '']
                      .filter(Boolean)
                      .join(' ')
                  });
                })
              }
            >
              {busy === 'move' ? 'Moving…' : 'Move from EasyPanel'}
            </Button>
          </Notice>
        )}
        {canDelete.length > 0 && (
          <Notice tone="ok" title="You can delete these from EasyPanel">
            {canDelete.map((x) => x.name).join(', ')}: Stitch uses its own now. Delete them in EasyPanel → Environment whenever you next redeploy.
          </Notice>
        )}
        {labels.map((l) => {
          const f = fieldOf(l.key);
          return (
            <div key={l.key}>
              <Field
                label={l.label}
                spellCheck={false}
                autoComplete={f.secret ? 'new-password' : 'off'}
                {...(f.secret ? { type: 'password' } : {})}
                disabled={f.secret && !v.canSeal}
                placeholder={f.secret && f.set ? 'Set (leave empty to keep it)' : (l.placeholder ?? '')}
                value={shown(l.key)}
                onChange={(e) => setTyped({ ...typed, [l.key]: e.target.value })}
                {...(l.hint ? { hint: l.hint } : {})}
              />
              <small className="muted" style={{ display: 'block', marginTop: -8, marginBottom: 12 }}>
                {f.source === 'stitch'
                  ? `Saved in Stitch${f.changed ? ` · ${shortWhen(f.changed.at)}${f.changed.byName ? ` by ${f.changed.byName}` : ''}` : ''}`
                  : f.source === 'easypanel'
                    ? `From EasyPanel (${f.env})`
                    : 'Not set'}
              </small>
            </div>
          );
        })}
        {problem && <Notice tone="crit">{problem}</Notice>}
        {note && <Notice tone={note.ok ? 'ok' : 'warn'}>{note.text}</Notice>}
        <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
          <Button type="submit" variant="primary" disabled={!changed || busy !== ''}>
            {busy === 'save' ? 'Saving…' : 'Save'}
          </Button>
          <Button
            disabled={busy !== ''}
            onClick={() =>
              void run('test', async () => {
                const r = await api.test(change());
                setNote({ ok: r.ok, text: r.message });
              })
            }
          >
            {busy === 'test' ? 'Testing…' : 'Test connection'}
          </Button>
          {saved.length > 0 && (
            <Button
              variant="ghost"
              disabled={busy !== ''}
              onClick={() =>
                void run('remove', async () => {
                  setView(await api.save(Object.fromEntries(saved.map((f) => [f.key, null]))));
                  setTyped({});
                  setNote({ ok: true, text: 'Removed what was saved in Stitch. EasyPanel’s variables count again, if they’re set.' });
                })
              }
            >
              Remove Stitch’s
            </Button>
          )}
        </div>
        {v.updatedAt && (
          <p className="muted small" style={{ marginBottom: 0 }}>
            Last changed {shortWhen(v.updatedAt)}
            {v.updatedByName ? ` by ${v.updatedByName}` : ''}.
          </p>
        )}
      </form>
    </Card>
  );
}

/** Stitch Admin → System → LiveKit (2845): Patches, meetings and calls' sound. */
export function LiveKitCard() {
  return (
    <SettingsGroupCard
      title="LiveKit"
      path="livekit"
      onLabel="Patches on"
      offLabel="Patches off"
      restartWhat="Patches"
      intro="Patches, meetings and the sound of calls in the Stitch apps go through LiveKit. Its addresses, API key and secret can be kept here instead of in EasyPanel. A change takes effect within a few seconds, with no redeploy; people already in a Patch or meeting stay on the server they joined until they leave."
      labels={[
        { key: 'url', label: 'Address (for browsers)', placeholder: 'wss://livekit.example.com', hint: 'wss://, as the LiveKit installer printed it.' },
        { key: 'apiUrl', label: 'API address (for Stitch)', placeholder: 'Leave empty to use the address above as https', hint: 'Only when Stitch reaches LiveKit another way, like http://livekit:7880 inside EasyPanel.' },
        { key: 'apiKey', label: 'API key', placeholder: 'APIxxxxxxxx' },
        { key: 'apiSecret', label: 'API secret', placeholder: 'The secret from LiveKit’s config' }
      ]}
    />
  );
}

/** Stitch Admin → System → Email settings (2845): how Stitch sends its emails, and the sender. */
export function MailSettingsCard() {
  return (
    <SettingsGroupCard
      title="Email settings"
      path="mail"
      onLabel="Email on"
      offLabel="Email off"
      restartWhat="Emails"
      intro="Invitations, password resets and voicemail emails go out through an SMTP server or Resend. Fill in one of them, and the sender. A change is used by the next email, with no redeploy, in the portal and the call records service alike."
      labels={[
        { key: 'resendApiKey', label: 'Resend API key', placeholder: 're_…', hint: 'From Resend → API Keys, with sending access. Or use an SMTP server below instead.' },
        { key: 'resendApiUrl', label: 'Resend API address', placeholder: 'Leave empty for https://api.resend.com' },
        { key: 'smtpUrl', label: 'SMTP server', placeholder: 'smtps://user:password@smtp.example.com:465', hint: 'Only instead of Resend. Its password is kept locked with the rest of the address.' },
        { key: 'from', label: 'Sender', placeholder: 'Stitch <portal@example.com>', hint: 'On a domain your email service has verified.' }
      ]}
    />
  );
}

/** Stitch Admin → System → Block Storage (2845): the B2 key and bucket for files, pictures and recordings. */
export function BlockStorageCard() {
  return (
    <SettingsGroupCard
      title="Block Storage"
      path="block-storage"
      onLabel="Block Storage on"
      offLabel="Kept in the database"
      restartWhat="Block Storage"
      intro="Files in messages, pictures, text attachments and session recordings are kept in Stitch Block Storage (a Backblaze B2 bucket). Its key and bucket can be kept here instead of in EasyPanel. A change is used by the next upload or download, with no redeploy. Files already kept stay where they are, so a new key must still be able to read the bucket they’re in."
      labels={[
        { key: 'keyId', label: 'Key ID', placeholder: 'keyID' },
        { key: 'applicationKey', label: 'Application key', placeholder: 'applicationKey', hint: 'Shown once by B2 when the key is made. It needs read, write and delete on the bucket.' },
        { key: 'bucketId', label: 'Bucket ID', placeholder: 'From the bucket’s page in B2' },
        { key: 'bucketName', label: 'Bucket name', placeholder: 'stitch-files' }
      ]}
    />
  );
}

/** Stitch Admin → System → AI (2845): the Claude API key, and the models Leland and smart messaging use. */
export function AiSettingsCard() {
  return (
    <SettingsGroupCard
      title="AI (Claude)"
      path="ai"
      onLabel="Leland on"
      offLabel="Leland off"
      restartWhat="Leland and the AI features"
      intro="Leland, smart messaging’s catch-ups and texting registration’s checks use the Claude API. Its key and the models can be kept here instead of in EasyPanel. A new key or model is used by the next request, with no redeploy."
      labels={[
        { key: 'apiKey', label: 'Claude API key', placeholder: 'sk-ant-…', hint: 'From the Claude Console → API keys.' },
        { key: 'lelandModel', label: 'Leland’s model', placeholder: 'claude-sonnet-5-5' },
        { key: 'smartModel', label: 'Smart messaging’s model', placeholder: 'Leave empty to use Leland’s' }
      ]}
    />
  );
}

/** Stitch Admin → System → Transcripts (2845): the speech-to-text service for voicemail and call recordings. */
export function TranscriptsSettingsCard() {
  return (
    <SettingsGroupCard
      title="Transcripts"
      path="transcripts"
      onLabel="Transcripts on"
      offLabel="Transcripts off"
      restartWhat="Transcripts"
      intro="Voicemail and call recording transcripts come from a speech-to-text service (Whisper, or any service that works like OpenAI’s). Its address, key, model and language can be kept here instead of in EasyPanel, for the portal and the call records service alike. A change is used by the next transcript, with no redeploy."
      labels={[
        { key: 'url', label: 'Address', placeholder: 'https://stt.example.com or http://transcriber:8000' },
        { key: 'key', label: 'Key', placeholder: 'Only if the service asks for one' },
        { key: 'model', label: 'Model', placeholder: 'whisper-1' },
        { key: 'language', label: 'Language', placeholder: 'Leave empty to let the service tell', hint: 'A two-letter code, like en.' }
      ]}
    />
  );
}
